import LegalDocShell from "@/components/legal/LegalDocShell";
import { buildLegalSections } from "@/lib/legalRenderer";

// Full 37-section AI Use & Agricultural Disclaimer Policy.
const SECTIONS = buildLegalSections([
  { id: "purpose", title: "1. Purpose of AI on NaturePulse", body:
`NaturePulse may use AI to help users better organize, understand, and explore observations about nature and agriculture. AI may support features such as:
- Plant identification
- Tree identification
- Flower identification
- Bird identification
- Animal identification
- Insect identification
- Fish or aquatic-life identification
- Crop identification
- Weed identification
- Pest identification
- Possible plant-disease recognition
- Image classification
- Suggested titles
- Suggested descriptions
- Tags and categories
- Translation
- Summaries
- Search
- Recommendations
- Geographic organization
- Pattern detection
- Agricultural information
- Research assistance
- Moderation support
- Duplicate detection
- Content organization

AI is intended to assist users, not replace human judgment.`
  },
  { id: "ai-errors", title: "2. AI Can Be Wrong", body:
`Artificial intelligence systems can make errors. AI may:
- Misidentify a plant
- Misidentify an animal
- Confuse similar species
- Misidentify an insect
- Misidentify a crop
- Suggest an incorrect plant disease
- Suggest the wrong pest
- Misinterpret visual symptoms
- Provide outdated information
- Misunderstand a question
- Generate incomplete information
- Produce information that sounds confident but is incorrect

Users should treat AI output as a suggestion or starting point, not guaranteed fact.`
  },
  { id: "verification", title: "3. Human Verification Is Important", body:
`Whenever information matters for safety, health, agriculture, wildlife, food, finances, or legal compliance, users should independently verify the information. Verification may include consulting:
- Agricultural extension officers
- Agronomists
- Veterinarians
- Botanists
- Biologists
- Entomologists
- Plant pathologists
- Government agricultural agencies
- Universities
- Certified pesticide professionals
- Conservation authorities
- Weather authorities
- Other qualified experts

NaturePulse encourages community verification and expert review where available.`
  },
  { id: "farmers-pulse-ai", title: "4. Farmers Pulse AI", body:
`Farmers Pulse may use AI to assist farmers and growers with questions involving:
- Crops
- Soil
- Irrigation
- Planting
- Harvesting
- Fertility
- Pests
- Plant diseases
- Weeds
- Weather
- Drought
- Flooding
- Pollinators
- Livestock
- Agricultural practices
- Market information
- Farm records

Farmers Pulse AI is not a licensed agricultural consultant, veterinarian, pesticide adviser, financial adviser, or government authority.`
  },
  { id: "local-conditions", title: "5. Agricultural Conditions Vary by Location", body:
`Agriculture is highly local. A recommendation that may be appropriate in one place may be unsuitable in another. Conditions vary based on:
- Country
- State or province
- District or county
- Elevation
- Climate
- Season
- Soil
- Crop variety
- Rainfall
- Irrigation
- Local pests
- Local diseases
- Farming methods
- Regulations

Users should always consider local conditions before acting on agricultural information.`
  },
  { id: "crop-disease", title: "6. Crop Disease Identification", body:
`AI may suggest possible causes of crop symptoms based on photographs or descriptions. Examples may include:
- Fungal disease
- Bacterial disease
- Viral disease
- Nutrient deficiency
- Insect damage
- Water stress
- Heat stress
- Cold injury
- Chemical injury
- Other environmental causes

A photograph alone may not be sufficient for an accurate diagnosis. Multiple unrelated problems can produce similar symptoms. For important crop decisions, seek confirmation from a qualified agricultural or plant-health professional.`
  },
  { id: "pest-id", title: "7. Pest Identification", body:
`AI may help identify possible pests. However, similar insects may have very different agricultural effects. Some insects are beneficial pollinators or natural predators of crop pests.

Do not apply pesticides solely because an AI system suggests that an insect is harmful. Verify identification before taking action.`
  },
  { id: "pesticides", title: "8. Pesticides, Herbicides, and Fungicides", body:
`NaturePulse AI may discuss agricultural chemicals for informational purposes, but it does not prescribe or authorize their use. Users must independently verify:
- Whether a product is approved in their jurisdiction
- Whether it is approved for the crop involved
- Permitted application rates
- Timing
- Protective equipment requirements
- Re-entry intervals
- Pre-harvest intervals
- Environmental restrictions
- Pollinator protections
- Water protections
- Disposal requirements

Always follow the legally approved product label and applicable regulations. If AI output conflicts with a product label or government requirement, the official label and law control.`
  },
  { id: "fertilizer", title: "9. Fertilizer and Soil Recommendations", body:
`AI-generated fertilizer, nutrient, compost, or soil recommendations should be treated as general information. Actual requirements may depend on:
- Soil-test results
- Crop type
- Crop age
- Soil texture
- pH
- Organic matter
- Irrigation
- Climate
- Yield goals

When significant expense or crop health is involved, obtain appropriate soil testing and local professional guidance.`
  },
  { id: "livestock", title: "10. Livestock and Animal Information", body:
`Farmers Pulse may provide general AI-assisted information concerning livestock or poultry. This information is not veterinary advice.

Do not rely exclusively on NaturePulse AI to diagnose or treat:
- Animal diseases
- Infections
- Poisoning
- Injuries
- Reproductive problems
- Medication needs
- Feed-related illness
- Emergency conditions

Consult a qualified veterinarian or appropriate animal-health authority.`
  },
  { id: "weather", title: "11. Weather Information", body:
`NaturePulse and Farmers Pulse may display or summarize weather information from third-party sources. AI may help interpret forecasts or conditions.

Weather information can change rapidly and may be inaccurate. NaturePulse is not an official emergency-warning service.

For safety-critical decisions involving:
- Severe storms
- Floods
- Wildfires
- Lightning
- Frost
- Extreme heat
- Extreme cold
- Evacuation

use official government weather and emergency sources.`
  },
  { id: "markets", title: "12. Market and Price Information", body:
`AI may summarize or analyze agricultural market information. Such information may be delayed, incomplete, inaccurate, estimated, or geographically limited.

NaturePulse does not guarantee:
- Commodity prices
- Produce prices
- Input costs
- Market demand
- Buyer information
- Revenue forecasts

Users should verify market information independently before making commercial decisions.`
  },
  { id: "gov-programs", title: "13. Government Programs and Regulations", body:
`NaturePulse AI may summarize information about:
- Subsidies
- Grants
- Crop insurance
- Disaster assistance
- Permits
- Agricultural regulations
- Government programs

Programs and regulations may change. Users should verify important information directly with the relevant government agency before applying, spending money, changing practices, or relying upon eligibility information.`
  },
  { id: "edible", title: "14. AI and Edible Species", body:
`Never consume a wild plant, mushroom, berry, fruit, herb, fish, shellfish, or other organism solely because AI identifies it as edible. Some dangerous species closely resemble edible species. AI identification is not sufficient for food-safety decisions.`
  },
  { id: "wildlife", title: "15. AI and Wildlife", body:
`Do not rely exclusively on AI to determine whether wildlife is safe to approach, handle, feed, capture, or relocate. Observe wildlife responsibly and follow applicable wildlife laws and expert guidance.`
  },
  { id: "titles-descriptions", title: "16. AI-Generated Titles and Descriptions", body:
`NaturePulse may automatically suggest:
- Observation titles
- Captions
- Descriptions
- Tags
- Categories

Users should review such suggestions before publication. If AI introduces a factual error, users are encouraged to correct it.`
  },
  { id: "translation", title: "17. AI Translation", body:
`NaturePulse may use AI to translate content. Translations may contain errors, especially with:
- Scientific terms
- Local plant names
- Agricultural terminology
- Legal terms
- Regional dialects

Important information should be verified in the original language when possible.`
  },
  { id: "summaries", title: "18. AI Summaries", body:
`AI may summarize:
- News
- Research
- Articles
- Community discussions
- Observations
- Agricultural information

Summaries may omit context or contain inaccuracies. Users should review original sources when accuracy matters.`
  },
  { id: "research", title: "19. Research Assistance", body:
`AI may help organize or analyze NaturePulse observations. Researchers should not assume that AI-generated classifications or summaries satisfy scientific standards.

Researchers remain responsible for:
- Methodology
- Validation
- Sampling
- Data quality
- Statistical analysis
- Ethical requirements
- Interpretation

NaturePulse does not guarantee research-grade accuracy unless explicitly stated for a particular dataset or program.`
  },
  { id: "confidence", title: "20. Confidence Scores", body:
`NaturePulse may display AI confidence levels. Examples:
- Low confidence
- Possible match
- Likely match
- High confidence

Confidence scores are estimates produced by automated systems. A high confidence score does not guarantee accuracy.`
  },
  { id: "community-verification", title: "21. Community Verification", body:
`NaturePulse may allow community members to review AI-assisted identifications. An observation may show statuses such as:
- AI Suggested
- Community Suggested
- Community Verified
- Expert Verified
- Research Grade

These designations may change as new information becomes available.`
  },
  { id: "expert-verification", title: "22. Expert Verification", body:
`NaturePulse may identify certain users as experts or qualified contributors. Expert verification can improve reliability but does not create a guarantee by NaturePulse. Experts are responsible for their own professional opinions.`
  },
  { id: "ai-media", title: "23. AI-Generated Media", body:
`AI-generated images, videos, audio, or other synthetic media must not be presented as authentic field observations.

Synthetic content should be clearly labeled when used for educational or illustrative purposes. For example:
- AI-generated illustration — not an actual observation.`
  },
  { id: "ai-user-content", title: "24. AI and User Content", body:
`When users submit photographs, text, or other information to AI-enabled features, the content may be processed to:
- Identify objects
- Generate descriptions
- Suggest classifications
- Search related observations
- Moderate content
- Improve user experience

Processing may involve third-party technology providers acting on behalf of NaturePulse. Additional information is available in the Privacy Policy.`
  },
  { id: "sensitive-inputs", title: "25. Do Not Submit Unnecessary Sensitive Information to AI", body:
`Users should avoid entering sensitive personal information into AI prompts unless necessary for the feature. Examples include:
- Passwords
- Financial account information
- Government identification numbers
- Private medical information
- Confidential business information
- Exact private addresses`
  },
  { id: "emergencies", title: "26. AI Is Not Emergency Assistance", body:
`Do not use NaturePulse AI as a substitute for emergency services. If there is an immediate threat involving:
- Human safety
- Fire
- Flooding
- Severe weather
- Poisoning
- Dangerous wildlife
- Agricultural chemical exposure
- Animal emergency

contact the appropriate emergency or professional authority.`
  },
  { id: "no-medical", title: "27. No Medical Advice", body:
`NaturePulse AI does not provide medical diagnosis or treatment advice. If a nature or agricultural observation involves human health, such as:
- Poisonous plants
- Allergic reactions
- Insect bites
- Animal bites
- Chemical exposure
- Food poisoning

seek appropriate medical assistance.`
  },
  { id: "no-legal", title: "28. No Legal Advice", body:
`AI-generated information about agriculture, land, environmental regulations, wildlife, pesticides, permits, or government requirements is general information only. NaturePulse does not provide legal advice. Consult appropriate legal counsel or government authorities when necessary.`
  },
  { id: "no-financial", title: "29. No Financial Advice", body:
`NaturePulse does not provide financial, investment, tax, or commodity-trading advice. AI-generated estimates involving:
- Farm income
- Prices
- Yield value
- Costs
- Insurance
- Loans
- Investments

should be independently verified.`
  },
  { id: "external-info", title: "30. AI May Use External Information", body:
`NaturePulse AI may rely on or reference information from:
- Government sources
- Research institutions
- Public databases
- Third-party data providers
- Weather providers
- User contributions
- Other information sources

External information may contain errors or become outdated. Users should consult original authoritative sources when important.`
  },
  { id: "local-knowledge", title: "31. AI and Local Knowledge", body:
`NaturePulse recognizes that farmers, indigenous communities, gardeners, conservationists, and local residents may possess valuable knowledge that AI systems do not capture.

AI should complement—not automatically override—credible local and traditional knowledge. Users are encouraged to document local observations and explain regional context.`
  },
  { id: "responsible", title: "32. Responsible Use of AI", body:
`Users should not use NaturePulse AI to:
- Fabricate observations
- Generate fake wildlife sightings
- Create false pest outbreaks
- Manipulate agricultural information
- Impersonate experts
- Create deceptive scientific claims
- Misrepresent synthetic images as real observations
- Generate spam
- Harass others
- Circumvent NaturePulse policies

Misuse may result in content removal or account restrictions.`
  },
  { id: "moderation", title: "33. Automated Moderation", body:
`NaturePulse may use AI-assisted moderation to detect:
- Spam
- Harassment
- Illegal content
- Inappropriate imagery
- Fraud
- Duplicate content
- Potential misinformation

Automated moderation can make mistakes. Where available, users may report or appeal moderation decisions.`
  },
  { id: "changes-ai", title: "34. Changes to AI Features", body:
`Artificial intelligence technology changes rapidly. NaturePulse may:
- Add new AI capabilities
- Replace AI providers
- Change models
- Adjust confidence thresholds
- Modify automated workflows
- Remove AI functions
- Introduce additional safeguards

We may update this Policy as AI capabilities and risks evolve.`
  },
  { id: "limitation", title: "35. Limitation of Reliance", body:
`By using AI-enabled features, you acknowledge that automated information may be incomplete or incorrect. You are responsible for evaluating information before relying on it.

NaturePulse and Social Pulse Inc. do not guarantee the accuracy, completeness, reliability, or suitability of AI-generated output.`
  },
  { id: "ag-disclaimer", title: "36. Agricultural Disclaimer", body:
`All agricultural information provided through NaturePulse or Farmers Pulse is intended primarily for general educational and informational purposes. NaturePulse does not guarantee the outcome of any agricultural practice.

Farming involves risks including:
- Weather
- Crop failure
- Disease
- Pests
- Soil conditions
- Water availability
- Market fluctuations
- Equipment failure
- Regulatory changes
- Natural disasters

Users remain responsible for agricultural decisions made on their farms or property.`
  },
  { id: "contact", title: "37. Contact Us", body:
`Questions regarding AI use or agricultural information may be directed to:

NaturePulse.net, a division of Social Pulse Inc.
Website: [NaturePulse.net](https://naturepulse.net)
Support Email: support@naturepulse.net
Privacy Email: legal@naturepulse.net

Mailing Address:
Social Pulse Inc.
15166 SE Aston Loop
Portland, OR 97236
United States`
  },
]);

export default function AiPolicy() {
  return (
    <LegalDocShell
      eyebrow="NaturePulse · AI Use & Agricultural Disclaimer"
      title="AI Use & Agricultural Disclaimer Policy"
      effective="August 23, 2026"
      updated="August 23, 2026"
      intro={
        <>
          <p>NaturePulse.net, a division of Social Pulse Inc. ("NaturePulse," "we," "us," or "our") may use artificial intelligence, machine learning, automated analysis, and related technologies to support nature observation, agricultural information, research, search, classification, image analysis, and community features.</p>
          <p>This Policy explains how AI may be used on NaturePulse and Farmers Pulse, the limitations of AI-generated information, and the responsibilities of users when relying on automated suggestions.</p>
          <p>This Policy should be read together with the <a href="/legal/terms">Terms of Service</a>, Privacy Policy, <a href="/legal/cookies">Cookie Policy</a>, <a href="/legal/community">Community Guidelines</a>, and <a href="/legal/user-content">User-Generated Content Policy</a>.</p>
        </>
      }
      sections={SECTIONS}
    />
  );
}
